import { useState, useEffect } from 'react';
import { X, Loader, Route, Plus, Trash2, GripVertical } from 'lucide-react';
import { supabase, SoundStop } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

type CreateTrailModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onTrailCreated?: () => void;
};

type StopInput = {
  tempId: string;
  name: string;
  description: string;
  latitude: string;
  longitude: string;
  radius: string;
  youtube_url: string;
};

export function CreateTrailModal({
  isOpen,
  onClose,
  onTrailCreated
}: CreateTrailModalProps) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('easy');
  const [estimatedDuration, setEstimatedDuration] = useState('30');
  const [stops, setStops] = useState<StopInput[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const addStop = () => {
    setStops([
      ...stops,
      {
        tempId: Date.now().toString(),
        name: '',
        description: '',
        latitude: '',
        longitude: '',
        radius: '20',
        youtube_url: ''
      }
    ]);
  };

  const removeStop = (tempId: string) => {
    setStops(stops.filter(stop => stop.tempId !== tempId));
  };

  const updateStop = (tempId: string, field: keyof StopInput, value: string) => {
    setStops(stops.map(stop =>
      stop.tempId === tempId ? { ...stop, [field]: value } : stop
    ));
  };

  const moveStop = (index: number, direction: 'up' | 'down') => {
    const newStops = [...stops];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newStops.length) return;

    [newStops[index], newStops[targetIndex]] = [newStops[targetIndex], newStops[index]];
    setStops(newStops);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (stops.length === 0) {
      setError('Add at least one stop to the trail');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: trailData, error: trailError } = await supabase
        .from('sound_trails')
        .insert({
          user_id: user.id,
          name,
          description,
          difficulty_level: difficulty,
          estimated_duration: parseInt(estimatedDuration),
          is_active: true
        })
        .select()
        .single();

      if (trailError) throw trailError;

      const stopsData = stops.map((stop, index) => ({
        trail_id: trailData.id,
        name: stop.name,
        description: stop.description,
        latitude: parseFloat(stop.latitude),
        longitude: parseFloat(stop.longitude),
        radius: parseInt(stop.radius),
        youtube_url: stop.youtube_url,
        position: index
      }));

      const { error: stopsError } = await supabase
        .from('sound_stops')
        .insert(stopsData);

      if (stopsError) throw stopsError;

      onTrailCreated?.();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create trail');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setDifficulty('easy');
    setEstimatedDuration('30');
    setStops([]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Route className="w-6 h-6 text-green-600" />
              <h2 className="text-2xl font-bold">Create Sound Trail</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Trail Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., Historic Downtown Audio Tour"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Difficulty Level
                </label>
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Describe the trail experience..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Estimated Duration (minutes)
              </label>
              <input
                type="number"
                min="1"
                value={estimatedDuration}
                onChange={(e) => setEstimatedDuration(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Stops ({stops.length})</h3>
                <button
                  type="button"
                  onClick={addStop}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Stop
                </button>
              </div>

              {stops.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500 mb-2">No stops added yet</p>
                  <button
                    type="button"
                    onClick={addStop}
                    className="text-green-600 hover:text-green-700 font-medium"
                  >
                    Add your first stop
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {stops.map((stop, index) => (
                    <div key={stop.tempId} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <GripVertical className="w-5 h-5 text-gray-400" />
                          <span className="font-medium">Stop {index + 1}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => moveStop(index, 'up')}
                            disabled={index === 0}
                            className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => moveStop(index, 'down')}
                            disabled={index === stops.length - 1}
                            className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => removeStop(stop.tempId)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={stop.name}
                          onChange={(e) => updateStop(stop.tempId, 'name', e.target.value)}
                          placeholder="Stop name *"
                          required
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          value={stop.youtube_url}
                          onChange={(e) => updateStop(stop.tempId, 'youtube_url', e.target.value)}
                          placeholder="YouTube URL *"
                          required
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <input
                          type="number"
                          step="any"
                          value={stop.latitude}
                          onChange={(e) => updateStop(stop.tempId, 'latitude', e.target.value)}
                          placeholder="Latitude *"
                          required
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <input
                          type="number"
                          step="any"
                          value={stop.longitude}
                          onChange={(e) => updateStop(stop.tempId, 'longitude', e.target.value)}
                          placeholder="Longitude *"
                          required
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <input
                          type="number"
                          min="5"
                          max="50"
                          value={stop.radius}
                          onChange={(e) => updateStop(stop.tempId, 'radius', e.target.value)}
                          placeholder="Radius (5-50m)"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                        <textarea
                          value={stop.description}
                          onChange={(e) => updateStop(stop.tempId, 'description', e.target.value)}
                          placeholder="Stop description"
                          rows={1}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Route className="w-4 h-4" />
                    Create Trail
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
